// src/pages/Register/Register.js
import React, { useState } from 'react'
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Divider,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Grid,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Snackbar
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  PersonAdd,
  Home as HomeIcon,
  BusinessCenter,
  Person
} from '@mui/icons-material'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'

const Register = () => {
  const navigate = useNavigate()
  const { register: registerUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [userType, setUserType] = useState('owner') // 'owner', 'realtor' ou 'agency'
  const [successMessage, setSuccessMessage] = useState('')
  const [openSnackbar, setOpenSnackbar] = useState(false)

  // Estados para campos com máscara
  const [phone, setPhone] = useState('')
  const [cpf, setCpf] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [businessPhone, setBusinessPhone] = useState('')

  // Função para máscara de telefone: (11) 99999-9999
  const maskPhone = (value) => {
    if (!value) return ''
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 10) {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1')
    }
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1')
  }

  // Função para máscara de CPF: 000.000.000-00
  const maskCPF = (value) => {
    if (!value) return ''
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  }

  // Função para máscara de CNPJ: 00.000.000/0000-00
  const maskCNPJ = (value) => {
    if (!value) return ''
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  }

  // Handlers para atualizar os estados com máscara
  const handlePhoneChange = (e) => {
    setPhone(maskPhone(e.target.value))
  }

  const handleCpfChange = (e) => {
    setCpf(maskCPF(e.target.value))
  }

  const handleCnpjChange = (e) => {
    setCnpj(maskCNPJ(e.target.value))
  }

  const handleBusinessPhoneChange = (e) => {
    setBusinessPhone(maskPhone(e.target.value))
  }

  // Função para formatar erros da API
  const formatApiErrors = (errorData) => {
    // Se for uma string, retorna direto
    if (typeof errorData === 'string') {
      return errorData
    }

    // Se for um objeto com campos e arrays de erros
    if (typeof errorData === 'object' && errorData !== null) {
      const errorMessages = []

      for (const [field, messages] of Object.entries(errorData)) {
        if (Array.isArray(messages)) {
          // Traduz nomes de campos para português
          const fieldNames = {
            email: 'Email',
            phone: 'Telefone',
            cpf: 'CPF',
            cnpj: 'CNPJ',
            password: 'Senha',
            first_name: 'Nome',
            last_name: 'Sobrenome',
            creci: 'CRECI',
            company_name: 'Razão Social',
            business_phone: 'Telefone Comercial'
          }

          const fieldLabel = fieldNames[field] || field
          messages.forEach((msg) => {
            errorMessages.push(`${fieldLabel}: ${msg}`)
          })
        }
      }

      return errorMessages.length > 0
        ? errorMessages.join('\n')
        : 'Erro ao processar requisição'
    }

    return 'Erro desconhecido'
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    console.log('=== SUBMIT INTERCEPTADO ===')

    // Coletar dados do formulário
    const formData = new FormData(e.target)
    const data = {}
    for (let [key, value] of formData.entries()) {
      data[key] = value
    }

    // Converter para boolean baseado no tipo de usuário
    data.is_realtor = userType === 'realtor'
    data.is_agency = userType === 'agency'

    console.log('Dados do formulário:', data)

    // Validações
    const errors = []

    if (!data.first_name?.trim()) errors.push('Nome é obrigatório')
    if (!data.last_name?.trim()) errors.push('Sobrenome é obrigatório')
    if (!data.email?.trim()) errors.push('Email é obrigatório')
    if (!data.phone?.trim()) errors.push('Telefone é obrigatório')
    if (!data.password?.trim()) errors.push('Senha é obrigatória')
    if (!data.password_confirm?.trim())
      errors.push('Confirmação de senha é obrigatória')

    // Validação de senha: mínimo 8 caracteres e pelo menos 1 número
    if (data.password && data.password.length < 8) {
      errors.push('Senha deve ter no mínimo 8 caracteres')
    }
    if (data.password && !/\d/.test(data.password)) {
      errors.push('Senha deve conter pelo menos 1 número')
    }

    if (data.password !== data.password_confirm) {
      errors.push('Senhas não coincidem')
    }

    // Validar CRECI se for corretor
    if (userType === 'realtor' && !data.creci?.trim()) {
      errors.push('CRECI é obrigatório para corretores')
    }

    // Validar campos de imobiliária
    if (userType === 'agency') {
      if (!data.cnpj?.trim())
        errors.push('CNPJ é obrigatório para imobiliárias')
      if (!data.company_name?.trim())
        errors.push('Razão social é obrigatória para imobiliárias')
      if (!data.business_phone?.trim())
        errors.push('Telefone comercial é obrigatório para imobiliárias')
    }

    if (errors.length > 0) {
      console.log('ERROS ENCONTRADOS - BLOQUEANDO ENVIO:', errors)
      setError('❌ Corrija os seguintes campos:\n• ' + errors.join('\n• '))
      return
    }

    console.log('✅ Validação passou - enviando para API')
    setError('')
    submitForm(data)
  }

  const submitForm = async (data) => {
    console.log('submitForm executado com:', data)
    setLoading(true)

    try {
      const result = await registerUser(data)

      if (result.success) {
        // Exibe toast de sucesso
        const message = result.data?.message || 'Conta criada com sucesso!'
        const name = data.first_name || 'usuário'
        setSuccessMessage(`${message} Bem-vindo(a), ${name}!`)
        setOpenSnackbar(true)

        // Aguarda um pouco antes de redirecionar para o usuário ver o toast
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 1500)
      } else {
        // Formata os erros da API adequadamente
        const formattedError = formatApiErrors(result.error)
        setError(formattedError || 'Erro ao criar conta')
      }
    } catch (err) {
      setError('Erro inesperado ao criar conta')
      console.error('Erro no submitForm:', err)
    }

    setLoading(false)
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenSnackbar(false)
  }

  return (
    <Container component='main' maxWidth='md'>
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: { xs: 3, sm: 4 },
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <PersonAdd sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography
            component='h1'
            variant='h4'
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Criar Conta
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            textAlign='center'
            sx={{ mb: 0.5 }}
          >
            Crie sua conta para anunciar imóveis e gerenciar suas publicações.
          </Typography>
          <Typography variant='body2' color='text.disabled' sx={{ mb: 3 }}>
            Leva menos de 1 minuto.
          </Typography>

          {error && (
            <Alert severity='error' sx={{ width: '100%', mb: 3 }}>
              <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
            </Alert>
          )}

          <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {/* Seleção de Tipo de Usuário */}
            <FormControl component='fieldset' sx={{ width: '100%', mb: 3 }}>
              <FormLabel component='legend' sx={{ mb: 2, fontWeight: 600 }}>
                Tipo de Conta
              </FormLabel>
              <RadioGroup
                row
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                sx={{
                  display: 'flex',
                  gap: 2,
                  '& .MuiFormControlLabel-root': {
                    flex: 1,
                    margin: 0,
                    border: '2px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    padding: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  },
                  '& .Mui-checked + .MuiFormControlLabel-label': {
                    fontWeight: 600
                  }
                }}
              >
                <FormControlLabel
                  value='owner'
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person />
                      <Box>
                        <Typography variant='body1'>
                          Sou Proprietário
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          Anunciar meus imóveis
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{
                    borderColor:
                      userType === 'owner' ? 'primary.main' : 'divider',
                    bgcolor:
                      userType === 'owner' ? 'action.selected' : 'transparent'
                  }}
                />
                <FormControlLabel
                  value='realtor'
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessCenter />
                      <Box>
                        <Typography variant='body1'>Sou Corretor</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          Gerenciar imóveis profissionalmente
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{
                    borderColor:
                      userType === 'realtor' ? 'primary.main' : 'divider',
                    bgcolor:
                      userType === 'realtor' ? 'action.selected' : 'transparent'
                  }}
                />
                <FormControlLabel
                  value='agency'
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessCenter />
                      <Box>
                        <Typography variant='body1'>Sou Imobiliária</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          Empresa do ramo imobiliário
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{
                    borderColor:
                      userType === 'agency' ? 'primary.main' : 'divider',
                    bgcolor:
                      userType === 'agency' ? 'action.selected' : 'transparent'
                  }}
                />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ mb: 3 }} />

            {/* Dados Pessoais */}
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
              Dados Pessoais
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  name='first_name'
                  label='Nome'
                  autoComplete='given-name'
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  name='last_name'
                  label='Sobrenome'
                  autoComplete='family-name'
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  name='email'
                  label='Email'
                  type='email'
                  autoComplete='email'
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  name='phone'
                  label='Telefone'
                  autoComplete='tel'
                  placeholder='(11) 99999-9999'
                  helperText='Digite seu telefone com DDD'
                  value={phone}
                  onChange={handlePhoneChange}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name='cpf'
                  label='CPF (opcional)'
                  placeholder='000.000.000-00'
                  helperText='Formato: 000.000.000-00'
                  value={cpf}
                  onChange={handleCpfChange}
                />
              </Grid>

              {/* Campo CRECI - apenas para corretores */}
              {userType === 'realtor' && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    name='creci'
                    label='CRECI'
                    placeholder='12345-F'
                    helperText='Seu número de registro no CRECI'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <BusinessCenter />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              )}

              {/* Campos para Imobiliária */}
              {userType === 'agency' && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      required
                      fullWidth
                      name='cnpj'
                      label='CNPJ'
                      placeholder='00.000.000/0000-00'
                      helperText='Formato: 00.000.000/0000-00'
                      value={cnpj}
                      onChange={handleCnpjChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <BusinessCenter />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      required
                      fullWidth
                      name='company_name'
                      label='Razão Social'
                      placeholder='Nome da empresa'
                      helperText='Nome oficial da imobiliária'
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      required
                      fullWidth
                      name='business_phone'
                      label='Telefone Comercial'
                      placeholder='(11) 3333-4444'
                      helperText='Telefone da imobiliária'
                      value={businessPhone}
                      onChange={handleBusinessPhoneChange}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Senha */}
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
              Segurança
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  name='password'
                  label='Senha'
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='new-password'
                  helperText='Mínimo 8 caracteres, incluindo pelo menos 1 número'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge='end'
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  name='password_confirm'
                  label='Confirmar Senha'
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete='new-password'
                  helperText='Digite a senha novamente'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge='end'
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>

            <Button
              type='submit'
              fullWidth
              variant='contained'
              size='large'
              sx={{ mt: 4, mb: 2, py: 1.5 }}
              disabled={loading}
            >
              {loading ? 'Criando conta...' : 'Criar Conta'}
            </Button>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                Já tem uma conta?{' '}
                <Link
                  component={RouterLink}
                  to='/login'
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 600,
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Faça login
                </Link>
              </Typography>

              <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                <Link
                  component={RouterLink}
                  to='/'
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textDecoration: 'none',
                    color: 'text.secondary',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: 'primary.main'
                    }
                  }}
                >
                  <HomeIcon sx={{ fontSize: 16 }} />
                  Voltar ao início
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Toast de Sucesso */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity='success'
          variant='filled'
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default Register
